// Branch On Hand — renders per-branch stock availability for a product. Inserts a
// "Branch On Hand" tab after the pvc-vehiclecompatibility tab on the product page,
// or renders into an explicit container if one is present.
//
// Config (read from the TARGET element, not the script tag):
//   #parts-branch-on-hand  OR  [data-parts-branch-on-hand]  — the container
//   data-sku-selector="#product-sku"  (optional; overrides the SKU source)
//
// SKU source (first hit wins): the container's data-sku, then data-sku-selector /
// #product-sku, then any [data-sku] on the page.
//
// When no explicit container is present, the widget injects its own tab into the
// storefront product-page tab chrome (.nav-tabs-contain). On pages without that
// chrome and without an explicit container, init() is a clean no-op.

import { getTopDocument, esc, getSku } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';
import { loadFeatures, isBranch } from '../shared/identity.js';

function escAttr(s) {
	if (s == null) return '';
	return String(s)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function getSelectedBranch() {
	try { return (window.top || window).localStorage.getItem('SelectedBranchName') || ''; }
	catch (e) { try { return localStorage.getItem('SelectedBranchName') || ''; } catch (e2) { return ''; } }
}

// Open the storefront's own branch selector — prefers the documented API
// (window.SFUI.branchSelector.openDrawer from storefront.branch-selector.js),
// falling back to clicking #branch-selector-btn. Targets the TOP window since
// the widget is usually iframed.
function openBranchSelector() {
	var win = window;
	try { if (window.top) win = window.top; } catch (e) { /* cross-origin — use current */ }
	try {
		if (win.SFUI && win.SFUI.branchSelector && typeof win.SFUI.branchSelector.openDrawer === 'function') {
			win.SFUI.branchSelector.openDrawer();
			return;
		}
	} catch (e) { /* fall through */ }
	try {
		var btn = getTopDocument().getElementById('fulfilment-selector-btn') || getTopDocument().getElementById('branch-selector-btn');
		if (btn) { btn.click(); return; }
	} catch (e) { /* ignore */ }
	console.warn('PartsBranchOnHand: branch selector not found (window.SFUI.branchSelector / #branch-selector-btn).');
}

// Credit context via the shared credit guard. Degrades to null (→ show stock)
// when credit-guard.js isn't on the page. Waits briefly for the guard, since
// this script and credit-guard.js load in parallel and this can run first.
function _pfcgReady() {
	return !!(window.PFCreditGuard && typeof window.PFCreditGuard.getContext === 'function');
}
function fetchCreditContext() {
	if (_pfcgReady()) {
		try { return window.PFCreditGuard.getContext(); } catch (e) { return Promise.resolve(null); }
	}
	return new Promise(function (resolve) {
		var tries = 0;
		(function waitForGuard() {
			if (_pfcgReady()) {
				try { resolve(window.PFCreditGuard.getContext()); } catch (e) { resolve(null); }
				return;
			}
			if (++tries > 30) { resolve(null); return; } // ~3s, then fail open
			setTimeout(waitForGuard, 100);
		})();
	});
}

// Shown in place of the per-branch table when we shouldn't show stock. With
// no creditBranches it's the "no branch selected" prompt; with creditBranches
// it names the branch(es) where the visitor DOES have credit.
function renderSelectBranchPrompt(container, creditBranches) {
	var names = (creditBranches || [])
		.map(function (b) { return (b && (b.name || b.code)) || ''; })
		.filter(Boolean);
	var hasCredit = names.length > 0;
	var label = hasCredit
		? 'Please select your branch (' + esc(names.join(', ')) + ')'
		: 'Please select your branch first';
	var text = hasCredit
		? 'You don’t have credit at the selected branch. Choose a branch where you have credit.'
		: 'Select your branch to see stock availability.';
	container.innerHTML = ''
		+ '<div class="pboh-wrap">'
		+ '<div class="pboh-header"><p class="pboh-title">Branch On Hand</p><p class="pboh-subtitle">Stock availability per branch</p></div>'
		+ '<div class="pboh-select-branch">'
		+ '<p class="pboh-select-branch-text">' + text + '</p>'
		+ '<button type="button" class="pboh-select-branch-btn">'
		+ '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
		+ '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>'
		+ '<span>' + label + '</span>'
		+ '</button>'
		+ '</div></div>';
	var btn = container.querySelector('.pboh-select-branch-btn');
	if (btn) btn.addEventListener('click', function (e) { e.preventDefault(); openBranchSelector(); });
}

/**
 * Try to get user geolocation and compute distances.
 * Returns a promise that resolves to { lat, lon } or null if unavailable/denied.
 */
function getUserLocation() {
	return new Promise(function (resolve) {
		if (!navigator.geolocation) { resolve(null); return; }
		navigator.geolocation.getCurrentPosition(
			function (pos) { resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }); },
			function () { resolve(null); },
			{ timeout: 5000, maximumAge: 60000 }
		);
	});
}

/**
 * Haversine distance between two lat/lon points, in km.
 */
function haversineKm(lat1, lon1, lat2, lon2) {
	var R = 6371;
	var dLat = (lat2 - lat1) * Math.PI / 180;
	var dLon = (lon2 - lon1) * Math.PI / 180;
	var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
	        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
	        Math.sin(dLon / 2) * Math.sin(dLon / 2);
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function render(container, sku) {
	// The full branch-stock list always shows the items — no "select your branch
	// first" gate. The ONLY thing that still hides stock is the B2B credit gate:
	// a gated account user viewing a branch they're NOT registered at is prompted
	// to switch to one where they have credit. Everyone else (B2C, no branch
	// selected, credit here, or guard absent) sees the full per-branch stock.
	fetchCreditContext().then(function (ctx) {
		if (ctx && ctx.gated && !ctx.hasCreditHere) {
			renderSelectBranchPrompt(container, ctx.creditBranches);
			return;
		}
		_renderStock(container, sku);
	});
}

function _renderStock(container, sku) {
	container.innerHTML = '<div class="pboh-wrap"><div class="pboh-header"><p class="pboh-title">Branch On Hand</p><p class="pboh-subtitle">Stock availability per branch</p></div><div class="pboh-loading">Loading branch stock…</div></div>';

	// Fetch branches — try our API proxy first, fall back to storefront's own /api/branches.
	// `?all=1` returns the FULL branch list (GW-531 hidden branches included) so a
	// hidden branch's stock code still resolves to its name + coords here (e.g.
	// "OTTE" → "Ottery"). Branch On Hand is a stock DISPLAY, not a picker, so it must
	// label every warehouse_stock code; the branch selectors keep the filtered list.
	function fetchBranches() {
		return apiJson('/api/branches?all=1', null, null)
			.then(function (data) {
				if (Array.isArray(data) && data.length > 0) return data;
				throw new Error('empty');
			})
			.catch(function () {
				// Fallback: try the storefront's own /api/branches directly
				// The storefront URL is the origin of the parent page
				var storefrontOrigin = '';
				try { storefrontOrigin = (window.top || window).location.origin; } catch(e) {}
				if (!storefrontOrigin) return [];
				return fetch(storefrontOrigin + '/api/branches', { method: 'GET', headers: { 'Accept': 'application/json' } })
					.then(function (r) { return r.ok ? r.json() : []; })
					.catch(function () { return []; });
			});
	}

	// Fetch part data, branches list, and user location in parallel
	Promise.all([
		apiJson('/api/parts/' + encodeURIComponent(sku), null, null)
			.then(function (part) {
				if (!part) throw new Error('Parts API');
				return part;
			}),
		fetchBranches(),
		getUserLocation()
	])
	.then(function (results) {
		var part = results[0];
		var branchList = Array.isArray(results[1]) ? results[1] : [];
		var userLocation = results[2];  // { lat, lon } or null

		// Also try storefront page globals as a fallback source of branch data
		// OrderEazi storefronts may expose window.StoreBranches, window.branches, etc.
		if (branchList.length === 0) {
			var topWin = (function() { try { return window.top || window; } catch(e) { return window; } })();
			var globals = ['StoreBranches', 'storeBranches', 'branches', 'Branches', 'BRANCHES', 'storefront_branches'];
			for (var gi = 0; gi < globals.length; gi++) {
				var gVal = topWin[globals[gi]];
				if (Array.isArray(gVal) && gVal.length > 0) { branchList = gVal; break; }
			}
		}

		// Try to enrich branch list from the storefront's own /api/branches if we have few entries
		// This is async but we can patch the map after render — skip for now and just inject
		// known selected branch from localStorage into the map so it always resolves
		// Only SelectedBranchName is owned by the storefront (legacy
		// SelectedBranch/SelectedBranchCode are stale parts-finder keys).
		var selectedBranchName = '';
		var selectedBranchCode = '';
		try {
			selectedBranchName = (window.top || window).localStorage.getItem('SelectedBranchName') || '';
		} catch(e) {
			try { selectedBranchName = localStorage.getItem('SelectedBranchName') || ''; } catch(e2) {}
		}

		// Build lookup keyed by BOTH branchShortCode and branchName (uppercase)
		// so warehouse_stock short codes (e.g. "SILV") resolve to full names (e.g. "Silverton")
		var branchMap = {};
		branchList.forEach(function (b) {
			var entry = {
				name: (b.branchName || b.name || b.branchCode || b.branchShortCode || '').trim(),
				lat: parseFloat(b.latitude || b.Latitude || ''),
				lon: parseFloat(b.longitude || b.Longitude || ''),
			};
			if (!entry.name) return;
			// Index by short code (e.g. "SILV")
			var shortCode = (b.branchShortCode || b.branchCode || '').trim().toUpperCase();
			if (shortCode) branchMap[shortCode] = entry;
			// Also index by full name (e.g. "SILVERTON") as fallback
			var nameKey = entry.name.toUpperCase();
			if (nameKey && !branchMap[nameKey]) branchMap[nameKey] = entry;
		});

		// Index branchMap entries by name so we can do reverse lookups
		// Also index by any short code found in localStorage
		if (selectedBranchName) {
			var snUpper = selectedBranchName.trim().toUpperCase();
			if (!branchMap[snUpper]) {
				branchMap[snUpper] = { name: selectedBranchName.trim(), lat: NaN, lon: NaN };
			}
			if (selectedBranchCode) {
				var scUpper = selectedBranchCode.trim().toUpperCase();
				if (!branchMap[scUpper]) branchMap[scUpper] = branchMap[snUpper];
			}
		}

		var warehouseStock = (part && part.warehouse_stock && typeof part.warehouse_stock === 'object')
			? part.warehouse_stock : {};

		// Build the full branch key list from branchList (all branches), not just warehouseStock.
		// Branches absent from warehouseStock get qty 0 but still appear in the list.
		var branchNames;
		if (branchList.length > 0) {
			// Use every branch from the API list; key by shortCode if available, else name
			branchNames = branchList.map(function (b) {
				return (b.branchShortCode || b.branchCode || b.branchName || b.name || '').trim();
			}).filter(function (k) { return k.length > 0; });
			// Deduplicate while preserving order
			var seen = {};
			branchNames = branchNames.filter(function (k) {
				var u = k.toUpperCase();
				if (seen[u]) return false;
				seen[u] = true;
				return true;
			});
			// Ensure any warehouseStock keys not in branchList are also included
			Object.keys(warehouseStock).forEach(function (k) {
				var u = k.toUpperCase();
				if (!seen[u]) { seen[u] = true; branchNames.push(k); }
			});
		} else {
			branchNames = Object.keys(warehouseStock);
		}

		var wrap = container.querySelector('.pboh-wrap');
		if (!wrap) return;

		if (branchNames.length === 0) {
			wrap.innerHTML = '<div class="pboh-header"><p class="pboh-title">Branch On Hand</p><p class="pboh-subtitle">Stock availability per branch</p></div><div class="pboh-empty">No branch stock data available for this part.</div>';
			return;
		}

		var selectedBranch = getSelectedBranch().toLowerCase();

		// Helper: get display name for a key
		function getBranchDisplayName(key) {
			var info = branchMap[key.toUpperCase()];
			return info ? info.name : key;
		}

		// Internal warehouses (Head Office / HQ, Cape DC) are not real
		// customer-facing branches — exclude them from the list entirely and
		// roll their stock into the selected branch (see below). Same rule set
		// as storefront-hide-head-office.js.
		var HIDDEN_BRANCHES = ['headoffice', 'hq', 'capedc'];
		function _isHiddenBranch(key) {
			var keyNorm = key.toLowerCase().replace(/[\s_-]/g, '');
			if (HIDDEN_BRANCHES.indexOf(keyNorm) !== -1) return true;
			var nameNorm = getBranchDisplayName(key).toLowerCase().replace(/[\s_-]/g, '');
			return HIDDEN_BRANCHES.indexOf(nameNorm) !== -1;
		}
		var otherKeys = branchNames.filter(function (key) { return !_isHiddenBranch(key); });

		// Compute distance for a key (Infinity if no coords or no user location)
		function getDistKm(key) {
			var info = branchMap[key.toUpperCase()];
			if (info && !isNaN(info.lat) && !isNaN(info.lon) && userLocation) {
				return haversineKm(userLocation.lat, userLocation.lon, info.lat, info.lon);
			}
			return Infinity;
		}

		// Sort: selected branch first, then by distance (nearest first), then alphabetical
		otherKeys.sort(function (a, b) {
			var aSelected = a.toUpperCase() === selectedBranch.toUpperCase() || getBranchDisplayName(a).toLowerCase() === selectedBranch;
			var bSelected = b.toUpperCase() === selectedBranch.toUpperCase() || getBranchDisplayName(b).toLowerCase() === selectedBranch;
			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;
			var aDist = getDistKm(a);
			var bDist = getDistKm(b);
			if (aDist !== bDist) return aDist - bDist;
			return getBranchDisplayName(a).localeCompare(getBranchDisplayName(b));
		});

		var orderedKeys = otherKeys;
		var totalCount = orderedKeys.length;

		// Build HTML
		var html = [
			'<div class="pboh-header">',
			'<p class="pboh-title">Branch On Hand</p>',
			'<p class="pboh-subtitle">Live stock availability across all branches</p>',
			'</div>',
			'<div class="pboh-search-wrap">',
			'<span class="pboh-search-icon" aria-hidden="true">',
			'<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>',
			'</span>',
			'<input type="text" class="pboh-search" placeholder="Search branches…" aria-label="Filter branches">',
			'<button type="button" class="pboh-clear" aria-label="Clear search" title="Clear">',
			'<svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>',
			'</button>',
			'</div>',
			'<div class="pboh-toolbar">',
		'<p class="pboh-meta pboh-meta-count" style="margin:0;">Showing ' + totalCount + ' branch' + (totalCount !== 1 ? 'es' : '') + '</p>',
		'<label class="pboh-toggle-wrap" id="pboh-toggle-instock" title="Toggle: show in-stock only">',
		'<span class="pboh-toggle-label">In Stock Only</span>',
		'<div class="pboh-toggle-track on"><div class="pboh-toggle-thumb"></div></div>',
		'</label>',
		'</div>',
			'<div class="pboh-table-wrap"><div class="pboh-list-scroll">',
			'<table class="pboh-table"><thead><tr>',
			'<th>Branch</th>',
			'<th>Distance</th>',
			'<th>Availability</th>',
			'</tr></thead><tbody>',
		].join('');

		// Build a case-insensitive warehouseStock lookup map
		var stockByUpperKey = {};
		Object.keys(warehouseStock).forEach(function (k) {
			stockByUpperKey[k.toUpperCase()] = warehouseStock[k];
		});

		// Hidden branches (Head Office, Cape DC) aren't shown as their own rows;
		// roll their stock into the selected ("your") branch's displayed count below.
		var hiddenBranchQty = 0;
		Object.keys(warehouseStock).forEach(function (k) {
			if (_isHiddenBranch(k)) {
				var hq = warehouseStock[k];
				hiddenBranchQty += (typeof hq === 'number' && isFinite(hq)) ? hq : 0;
			}
		});

		// Branch (staff) users get Head Office as its own row below (status-only).
			var branchUser = isBranch();

			orderedKeys.forEach(function (key, idx) {
			var keyUpper = key.toUpperCase();
			var branchInfo = branchMap[keyUpper] || null;
			var displayName = branchInfo ? branchInfo.name : key;
			var isSelected = keyUpper === selectedBranch.toUpperCase() ||
				displayName.toLowerCase() === selectedBranch;
			// Selected ("your") branch shows its own stock + hidden-branch stock.
			var qty = (stockByUpperKey[keyUpper] || 0) + ((isSelected && !branchUser) ? hiddenBranchQty : 0);
			var isAvailable = qty > 0;

			// Compute real distance if we have branch coords and user location
			var distText = '-';
			if (branchInfo && !isNaN(branchInfo.lat) && !isNaN(branchInfo.lon)) {
				if (userLocation) {
					var km = haversineKm(userLocation.lat, userLocation.lon, branchInfo.lat, branchInfo.lon);
					distText = km.toFixed(1) + ' km';
				} else {
					distText = '- km';
				}
			}

			var rowClasses = ['pboh-row'];
			if (isSelected) rowClasses.push('pboh-row-selected');
			if (!isAvailable) rowClasses.push('pboh-row-oos');

			var branchLabel = esc(displayName);
			if (isSelected) {
				branchLabel += ' <span class="pboh-selected-label">Your branch</span>';
			}

			// Search matches on display name, short code, and selected branch name if this is the selected branch
			var searchVal = escAttr((displayName + ' ' + key + (isSelected && selectedBranchName ? ' ' + selectedBranchName : '')).toLowerCase());

			html += '<tr class="' + rowClasses.join(' ') + '" data-search="' + searchVal + '" data-instock="' + (isAvailable ? '1' : '0') + '">';
			html += '<td class="pboh-cell-branch">' + branchLabel + '</td>';
			html += '<td class="pboh-cell-dist">' + esc(distText) + '</td>';
			html += '<td class="pboh-cell-avail">';
			html += '<span class="pboh-badge ' + (isAvailable ? 'avail' : 'unavail') + '">';
			// showStockQuantity gates the numeric count; off → in/out status only.
			var showQ = (((window.__PARTS_FINDER_FEATURES__ || {}).showStockQuantity) !== false) && !branchUser;
			html += showQ
				? (isAvailable ? qty + ' Available' : 'Not Available')
				: (isAvailable ? 'In Stock' : 'Out of Stock');
			html += '</span>';
			html += '</td>';
			html += '</tr>';
		});

		// Head Office row — branch users only. Status-only, so a branch user can
			// see whether the central warehouse can back-fill their branch. Other
			// roles never see Head Office here (its stock is rolled into the branch).
			if (branchUser) {
				var hoAvail = hiddenBranchQty > 0;
				html += '<tr class="pboh-row pboh-head-office' + (hoAvail ? '' : ' pboh-row-oos') + '" data-search="head office" data-instock="' + (hoAvail ? '1' : '0') + '">';
				html += '<td class="pboh-cell-branch">Head Office <span class="pboh-selected-label">Backup</span></td>';
				html += '<td class="pboh-cell-dist">-</td>';
				html += '<td class="pboh-cell-avail"><span class="pboh-badge ' + (hoAvail ? 'avail' : 'unavail') + '">' + (hoAvail ? 'In Stock' : 'Out of Stock') + '</span></td>';
				html += '</tr>';
			}

			html += '</tbody></table></div></div>';
		wrap.innerHTML = html;

		// Wire up search/filter + in-stock toggle
		var searchInput = wrap.querySelector('.pboh-search');
		var clearBtn = wrap.querySelector('.pboh-clear');
		var metaEl = wrap.querySelector('.pboh-meta-count');
		var toggleWrap = wrap.querySelector('.pboh-toggle-wrap');
		var toggleTrack = wrap.querySelector('.pboh-toggle-track');
		var rows = wrap.querySelectorAll('.pboh-row');
		var tableWrap = wrap.querySelector('.pboh-table-wrap');
		var showInStockOnly = true;

		function updateFilter() {
			var q = (searchInput.value || '').trim().toLowerCase();
			// When a search query is active, show all matching branches regardless of stock
			// so users can always find a specific branch
			var applyStockFilter = showInStockOnly && !q;
			var visible = 0;
			rows.forEach(function (tr) {
				var matchesSearch = !q || tr.getAttribute('data-search').indexOf(q) !== -1;
				var matchesFilter = !applyStockFilter || tr.getAttribute('data-instock') === '1';
				var show = matchesSearch && matchesFilter;
				tr.classList.toggle('pboh-row-hidden', !show);
				if (show) visible++;
			});
			metaEl.textContent = q
				? 'Showing ' + visible + ' of ' + totalCount + ' branch' + (totalCount !== 1 ? 'es' : '')
				: 'Showing ' + visible + ' branch' + (visible !== 1 ? 'es' : '');
			clearBtn.classList.toggle('visible', q.length > 0);
			var noMatch = wrap.querySelector('.pboh-no-matches');
			if (visible === 0) {
				if (!noMatch) {
					noMatch = document.createElement('p');
					noMatch.className = 'pboh-no-matches';
					noMatch.textContent = q ? 'No branches match your search.' : 'No branches with stock available.';
					tableWrap.parentNode.insertBefore(noMatch, tableWrap.nextSibling);
				}
				noMatch.textContent = q ? 'No branches match your search.' : 'No branches with stock available.';
				noMatch.style.display = 'block';
				tableWrap.style.display = 'none';
			} else {
				if (noMatch) noMatch.style.display = 'none';
				tableWrap.style.display = '';
			}
		}

		if (toggleWrap) {
			toggleWrap.addEventListener('click', function () {
				showInStockOnly = !showInStockOnly;
				if (toggleTrack) toggleTrack.classList.toggle('on', showInStockOnly);
				updateFilter();
			});
		}

		searchInput.addEventListener('input', updateFilter);
		searchInput.addEventListener('keydown', function (e) {
			if (e.key === 'Escape') { searchInput.value = ''; updateFilter(); searchInput.focus(); }
		});
		clearBtn.addEventListener('click', function () { searchInput.value = ''; updateFilter(); searchInput.focus(); });
		updateFilter();
	})
	.catch(function (err) {
		var wrap = container.querySelector('.pboh-wrap');
		if (wrap) wrap.innerHTML = '<div class="pboh-error">Could not load branch stock data.</div>';
		console.warn('PartsBranchOnHand: fetch failed', err);
	});
}

/**
 * Create the "Branch On Hand" tab and panel, inserted after the
 * pvc-vehiclecompatibility tab.
 *
 * Storefront8 uses:
 *   - Tab triggers: button.plugin-tab-btn[data-tab] inside .nav-tabs-contain
 *   - Tab panels:   div.plugin-tab-pane inside .plugin-tab-content
 */
function ensureTabContainer() {
	var topDoc = getTopDocument();
	var tabId = 'pboh-branchonhand';
	var existing = topDoc.getElementById(tabId);
	if (existing) return existing;

	var navTabsContain = topDoc.querySelector('.nav-tabs-contain');
	if (!navTabsContain) return null;

	// Tab trigger list — button[data-tab] siblings live directly in .nav-tabs-contain
	// or inside a child .nav.nav-tabs wrapper depending on theme version.
	var tabList = navTabsContain.querySelector('.nav.nav-tabs') || navTabsContain;

	// Panel container — Storefront8 uses .plugin-tab-content
	var tabContent = topDoc.querySelector('.plugin-tab-content');
	if (!tabContent) {
		// Fallback: legacy .tab-content inside the nav-tabs container
		tabContent = navTabsContain.querySelector('.tab-content');
	}
	if (!tabContent) return null;

	// Determine panel class to match existing sibling panels
	var existingPane = tabContent.querySelector('[role="tabpanel"]');
	var paneClass = existingPane ? existingPane.className.replace(/\bactive\b/g, '').trim() : 'plugin-tab-pane';

	// ── Create tab trigger button (matches .plugin-tab-btn pattern) ──────
	var tabBtn = topDoc.createElement('button');
	tabBtn.type = 'button';
	tabBtn.className = 'plugin-tab-btn';
	tabBtn.setAttribute('data-tab', tabId);
	tabBtn.setAttribute('id', 'pboh-tab-btn');
	tabBtn.setAttribute('fdprocessedid', 'pboh-tab');
	tabBtn.textContent = 'Branch On Hand';

	// Insert after the vehicle-compatibility trigger if present
	var vcTrigger = tabList.querySelector('[data-tab="pvc-vehiclecompatibility"]');
	if (vcTrigger && vcTrigger.nextSibling) {
		tabList.insertBefore(tabBtn, vcTrigger.nextSibling);
	} else {
		tabList.appendChild(tabBtn);
	}

	// ── Create tab panel ─────────────────────────────────────────────────
	var panel = topDoc.createElement('div');
	panel.setAttribute('role', 'tabpanel');
	panel.className = paneClass;
	panel.id = tabId;

	// Insert after vehicle-compatibility panel if present
	var vcPanel = tabContent.querySelector('#pvc-vehiclecompatibility');
	if (vcPanel && vcPanel.nextSibling) {
		tabContent.insertBefore(panel, vcPanel.nextSibling);
	} else {
		tabContent.appendChild(panel);
	}

	return panel;
}

// Explicit container, if the page provides one. When absent, init() falls back
// to injecting the widget's own product-page tab (ensureTabContainer).
function findContainer() {
	var doc = getTopDocument();
	return doc.getElementById('parts-branch-on-hand') || doc.querySelector('[data-parts-branch-on-hand]');
}

function resolveSku(container) {
	// 1) explicit on the container
	var direct = container && (container.getAttribute('data-sku') || '');
	if (direct && direct.trim()) return direct.trim();
	// 2) configured selector on the container, else #product-sku, then any [data-sku]
	var selector = (container && container.getAttribute('data-sku-selector')) || '#product-sku';
	return getSku(selector);
}

export function init() {
	// Resolve any explicit container first so we can read its per-instance config
	// (data-sku / data-sku-selector). The container may legitimately be absent —
	// on product pages the widget injects its own tab instead (below).
	var container = findContainer();

	var sku = resolveSku(container);
	if (!sku) {
		// No SKU on this page → nothing to render (safe no-op on non-product pages).
		return;
	}

	// Gate the entire widget on `showBranchStock` — if the visitor's role disables
	// branch-stock visibility, skip the render entirely (no DOM, no tab created).
	// Check SKU + feature BEFORE creating any tab so we never inject an empty tab.
	loadFeatures().then(function (features) {
		if (features && features.showBranchStock === false) {
			console.log('PartsBranchOnHand: feature `showBranchStock` is disabled for this role; skipping render.');
			return;
		}
		// Use the explicit container if present, else inject the product-page tab.
		// Both may be absent (page has neither) → clean no-op.
		var target = container || ensureTabContainer();
		if (!target) return;
		render(target, sku);
	});
}

export var id = 'branch-on-hand';
